import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useI18n, LANGS } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2 } from "lucide-react";

// Neutral fallback (muted architectural facade) — only used until an admin sets the hero photo in the CMS.
const HERO_FALLBACK = "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

export default function Login() {
  const { t, lang, changeLang } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const { data: content } = useQuery({ queryKey: ["public-content"], queryFn: () => api.get("/public/content").then((r) => r.data) });
  const heroImg = mediaUrl(content?.hero_image) || HERO_FALLBACK;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || t("login.error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex font-sans">
      {/* Left form */}
      <div className="w-full lg:w-[46%] flex flex-col justify-center px-8 sm:px-16 bg-linen relative">
        <div className="absolute top-6 right-6 flex items-center gap-1 bg-white rounded-full border border-border p-1">
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => changeLang(l.code)} data-testid={`login-lang-${l.code}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${lang === l.code ? "bg-teak text-white" : "text-muted-foreground"}`}>
              {l.label}
            </button>
          ))}
        </div>
        <div className="max-w-sm w-full mx-auto">
          <div className="flex items-center gap-3 mb-10 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-11 h-11 rounded-md bg-teak flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="font-semibold text-teak text-lg leading-tight">{t("appName")}</div>
              <div className="text-xs text-muted-foreground">{t("appSub")}</div>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak mb-2">{t("login.title")}</h1>
          <p className="text-muted-foreground mb-8">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email" placeholder="alain@maison-thai.pro" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("login.password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password" placeholder="••••••••" className="h-11" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 bg-teak hover:bg-teak-hover text-white rounded-md">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("login.signin")}
            </Button>
          </form>
        </div>
      </div>
      {/* Right hero */}
      <div className="hidden lg:block flex-1 relative">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-teak/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="text-2xl font-light leading-relaxed max-w-md">
            {t("appName")} — {t("appSub")}
          </p>
          <p className="text-white/70 mt-2 text-sm">NG Residence · Thaïlande</p>
        </div>
      </div>
    </div>
  );
}
